import { apiClient } from './api-client';
import { useAuthStore } from '@/features/auth/store';
import { PUBLIC_ANALYTICS_EVENT_TYPE_SET, type AnalyticsEventType } from '@bookclub/shared';

interface EventItem {
  type: string;
  payload?: Record<string, unknown>;
  timestamp: string;
  sessionId?: string;
}

// ─── Session ID ────────────────────────────────────────────────────────────

const SESSION_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
// ─── Batch queue ──────────────────────────────────────────────────────────

const queue: EventItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 50;

async function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_BATCH_SIZE);
  try {
    await apiClient.post('analytics/events', { json: { events: batch } }).json();
  } catch {
    // Analytics failures are silent — never break user flow
  }
}

function scheduledFlush() {
  flushTimer = null;
  void flush();
}

function scheduleFlush() {
  if (flushTimer === null) {
    flushTimer = setTimeout(scheduledFlush, FLUSH_INTERVAL_MS);
  }
}

// Flush on page unload (best-effort)
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void flush();
  });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Track a product analytics event.
 * Events are batched and sent every 5 seconds or on page unload.
 * Never throws — analytics must not affect user experience.
 */
export function trackEvent(
  type: AnalyticsEventType | string,
  payload?: Record<string, unknown>,
): void {
  if (!useAuthStore.getState().isAuthenticated && !PUBLIC_ANALYTICS_EVENT_TYPE_SET.has(type as AnalyticsEventType)) {
    return;
  }

  queue.push({
    type,
    payload,
    timestamp: new Date().toISOString(),
    sessionId: SESSION_ID,
  });
  scheduleFlush();
}

/**
 * Force-flush the event queue immediately (use on important transitions).
 */
export function flushEvents(): void {
  void flush();
}
